import type { FC } from 'react'
import { Chip, Stack, Typography } from '@mui/material'
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded'

import { SectionCard } from '~/shared/ui'
import { formatApiDate } from '~/shared/lib/date'
import type { JourneySummary, TimelineEvent } from './MemberTimelineService'

interface JourneySummaryCardProps {
  summary: JourneySummary | null
  isGenerating: boolean
  hasFailed: boolean
  events: TimelineEvent[]
  onCitationClick: (event: TimelineEvent) => void
}

/** What the card says while it has nothing settled to show. */
const progressLabel = (hasText: boolean) => (hasText ? 'Updating…' : 'Generating…')

/**
 * The AI catch-up paragraph above the feed.
 *
 * Shows the text it has while a fresher one is being written, rather than blanking: a summary
 * one event out of date is still most of the answer. Everything rendered here — the prose, the
 * coverage line, which events are cited — is decided server-side.
 */
const JourneySummaryCard: FC<JourneySummaryCardProps> = ({
  summary,
  isGenerating,
  hasFailed,
  events,
  onCitationClick,
}) => {
  const text = summary?.text ?? null
  // A settled summary with no text is a member whose feed holds nothing worth a paragraph,
  // which is a different thing from one still being written.
  const isEmpty = summary != null && !isGenerating && !hasFailed && text == null

  if (summary == null && !isGenerating && !hasFailed) return null

  const generatedAt = summary?.generated_at
    ? formatApiDate(summary.generated_at, 'MMM d, yyyy h:mm a')
    : null
  const citedEvents = (summary?.citations ?? [])
    .map(citation => events.find(event => event.id === citation))
    .filter((event): event is TimelineEvent => event != null)

  return (
    <SectionCard data-testid="journey-summary-card">
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1 }}>
        <AutoAwesomeRoundedIcon aria-hidden sx={{ fontSize: '1.8rem', color: 'text.secondary' }} />
        <Typography sx={{ fontSize: '1.3rem', fontWeight: 600, color: 'text.primary' }}>
          Journey summary
        </Typography>
        {isGenerating && (
          <Chip
            size="small"
            label={progressLabel(text != null)}
            data-testid="journey-summary-progress"
          />
        )}
      </Stack>

      {hasFailed && text == null ? (
        <Typography sx={{ fontSize: '1.3rem', color: 'text.secondary' }}>
          The summary could not be generated. The timeline below is unaffected.
        </Typography>
      ) : isEmpty ? (
        <Typography sx={{ fontSize: '1.3rem', color: 'text.secondary' }}>
          Nothing to summarize for this member yet.
        </Typography>
      ) : text == null ? (
        <Typography sx={{ fontSize: '1.3rem', color: 'text.secondary' }}>
          Reading this member&rsquo;s timeline…
        </Typography>
      ) : (
        <Typography sx={{ fontSize: '1.4rem', color: 'text.primary' }}>{text}</Typography>
      )}

      {citedEvents.length > 0 && (
        <Stack
          direction="row"
          spacing={0.75}
          sx={{ mt: 1.5, flexWrap: 'wrap', rowGap: 0.75 }}
          data-testid="journey-summary-citations"
        >
          {citedEvents.map(event => (
            <Chip
              key={event.id}
              size="small"
              variant="outlined"
              label={event.title}
              onClick={() => onCitationClick(event)}
              data-testid={`journey-summary-citation-${event.id}`}
            />
          ))}
        </Stack>
      )}

      {hasFailed && text != null && (
        <Typography sx={{ mt: 1, fontSize: '1.25rem', color: 'text.secondary' }}>
          Couldn&rsquo;t refresh this just now — the text above is the last one that worked.
        </Typography>
      )}

      <Stack
        direction="row"
        spacing={1}
        sx={{ mt: 1.5, alignItems: 'center', flexWrap: 'wrap', rowGap: 0.5 }}
      >
        <Typography sx={{ fontSize: '1.15rem', color: 'text.secondary' }}>
          AI-generated from this member&rsquo;s timeline. Check anything you act on.
        </Typography>
        {summary?.coverage && (
          <Typography sx={{ fontSize: '1.15rem', color: 'text.secondary' }}>
            {`· ${summary.coverage}`}
          </Typography>
        )}
        {generatedAt && (
          <Typography sx={{ fontSize: '1.15rem', color: 'text.secondary' }}>
            {`· ${generatedAt}`}
          </Typography>
        )}
      </Stack>
    </SectionCard>
  )
}

export default JourneySummaryCard
